import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Box,
  Button,
  FormControl,
  Link,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import { useCallback, useEffect, useRef, useState } from "react";

import { BACKEND_URL } from "../env";
import {
  checkAccountNameAvailability,
  checkEmailAvailability,
} from "../helpers/fetch-registration";
import { RegistrationFormData } from "../types/registration";
import { SuccessDialog } from "./SuccessDialog";

export interface RegistrationComponentState {
  isSubmitting: boolean;
  submitStatus: {
    type: "success" | "error" | null;
    message: string;
    code?: string;
  };
  showSuccessDialog: boolean;
  language: SupportedLanguage;
}

export type RegistrationComponentEvent =
  "register" | "close-success-dialog" | "clear-error" | "change-language";

type RegistrationComponentEventPayloads = {
  register?: RegistrationFormData;
  language?: SupportedLanguage;
};

const STEPS = [
  { key: "auth.registration.steps.email", number: 1 },
  { key: "auth.registration.steps.personalInfo", number: 2 },
  { key: "auth.registration.steps.companyInfo", number: 3 },
];

const languageOptions: {
  code: SupportedLanguage;
  label: string;
}[] = [
  { code: "de", label: "german" },
  { code: "en", label: "english" },
];

type RegistrationFormState = {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  accountName: string;
};

type TouchedState = {
  email?: boolean;
  firstName?: boolean;
  lastName?: boolean;
  password?: boolean;
  confirmPassword?: boolean;
  companyName?: boolean;
  accountName?: boolean;
};

const initialFormState: RegistrationFormState = {
  email: "",
  firstName: "",
  lastName: "",
  password: "",
  confirmPassword: "",
  companyName: "",
  accountName: "",
};

// Keep in sync with registration.dto.ts in home-portal-backend.
const PERSON_NAME_PATTERN = /^[\p{L}\p{M}\p{Zs}'’-]+$/u;
const COMPANY_NAME_PATTERN = /^[\p{L}\p{M}\p{N}\p{Zs}&.,'’()-]+$/u;
// Latin only: the backend turns the account name into an ASCII slug for Keycloak and app URLs.
const ACCOUNT_NAME_PATTERN = /^[\p{Script=Latin}\p{M}0-9\p{Zs}-]+$/u;
// Printable ASCII only, like the backend DTO: no umlauts, ß or €.
const PASSWORD_PATTERN = /^[\x20-\x7E]+$/;

// Mirror the backend's @MaxLength limits so input stops before the server rejects it.
const MAX_LENGTH = {
  email: 254,
  personName: 255,
  companyName: 255,
  accountName: 100,
};

// Backend error codes (registration.service.ts) to locale keys under auth.registration.errors.
const API_ERROR_KEYS: Record<string, string> = {
  email_taken: "emailAlreadyExists",
  email_invalid: "emailInvalid",
  account_name_taken: "accountNameTaken",
  account_name_invalid: "accountNameInvalid",
  account_name_too_short: "accountNameNoLatinLetters",
  account_name_too_long: "accountNameTooLong",
  validation_failed: "validationFailed",
};

const translate = (
  language: SupportedLanguage,
  key: string,
  fallback: string
) => {
  try {
    const msg = getLocalizedMessage(language, key);
    return msg && msg !== key ? msg : fallback;
  } catch {
    return fallback;
  }
};

const localizeApiError = (
  language: SupportedLanguage,
  code: string | undefined,
  fallback: string
) => {
  const key = code ? API_ERROR_KEYS[code] : undefined;
  return key
    ? translate(language, `auth.registration.errors.${key}`, fallback)
    : fallback;
};

// Code points, like class-validator's MinLength; `.length` counts "𠀀" as 2.
const charCount = (value: string) => [...value.trim()].length;

const GERMAN_LETTERS: Record<string, string> = {
  ä: "ae",
  ö: "oe",
  ü: "ue",
  Ä: "Ae",
  Ö: "Oe",
  Ü: "Ue",
  ß: "ss",
  ẞ: "SS",
};

const transliterateAccountName = (value: string) =>
  value
    .normalize("NFC")
    .replace(/[äöüÄÖÜßẞ]/g, (letter) => GERMAN_LETTERS[letter])
    .normalize("NFD")
    .replace(/\p{M}/gu, "");

const sanitizeAccountName = (value: string) =>
  transliterateAccountName(value)
    .replace(/[^\p{Script=Latin}0-9\s-]/gu, "")
    .replace(/\s+/g, " ")
    .trim();

export const RegistrationPage: PimoReactComponent<
  RegistrationComponentState,
  RegistrationComponentEvent,
  RegistrationComponentEventPayloads
> = ({
  fireEvent,
  isSubmitting,
  submitStatus,
  showSuccessDialog,
  language = "en",
}) => {
  const t = (key: string, fallback: string) =>
    translate(language, key, fallback);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] =
    useState<RegistrationFormState>(initialFormState);
  const [touchedFields, setTouchedFields] = useState<TouchedState>({});
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailCheckError, setEmailCheckError] = useState<string>("");
  const emailCheckRequestRef = useRef(0);
  const [isCheckingAccountName, setIsCheckingAccountName] = useState(false);
  const [accountNameCheckError, setAccountNameCheckError] =
    useState<string>("");
  const accountNameManuallyEdited = useRef(false);
  const accountNameCheckTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const accountNameCheckRequestRef = useRef(0);

  const cancelAccountNameAvailabilityCheck = useCallback(() => {
    if (accountNameCheckTimeoutRef.current) {
      clearTimeout(accountNameCheckTimeoutRef.current);
      accountNameCheckTimeoutRef.current = null;
    }
    accountNameCheckRequestRef.current += 1;
    setIsCheckingAccountName(false);
  }, []);

  // Debounced function to check account name availability
  const checkAccountNameAvailabilityDebounced = useCallback(
    (accountName: string) => {
      cancelAccountNameAvailabilityCheck();
      const requestId = accountNameCheckRequestRef.current;

      accountNameCheckTimeoutRef.current = setTimeout(async () => {
        setAccountNameCheckError("");
        setIsCheckingAccountName(true);
        const accountNameCheck =
          await checkAccountNameAvailability(accountName);

        if (accountNameCheckRequestRef.current !== requestId) {
          return;
        }

        setIsCheckingAccountName(false);

        if (!accountNameCheck.available) {
          setAccountNameCheckError(
            localizeApiError(
              language,
              accountNameCheck.error?.code,
              accountNameCheck.message ||
                `Account "${accountName}" already exists`
            )
          );
        }
      }, 500); // 500ms debounce
    },
    [cancelAccountNameAvailabilityCheck, language]
  );

  // Auto-fill account name from company name and re-check availability when it changes.
  const handleCompanyNameChange = (companyName: string) => {
    setFormData((prev) => ({
      ...prev,
      companyName,
    }));

    if (accountNameManuallyEdited.current) {
      return;
    }

    const sanitizedCompanyName = sanitizeAccountName(companyName);
    setFormData((prev) =>
      prev.accountName === sanitizedCompanyName
        ? prev
        : {
            ...prev,
            accountName: sanitizedCompanyName,
          }
    );

    if (sanitizedCompanyName.length >= 2) {
      checkAccountNameAvailabilityDebounced(sanitizedCompanyName);
    } else {
      cancelAccountNameAvailabilityCheck();
      setAccountNameCheckError("");
    }
  };

  // On a failed submit keep what the user typed and send them to the field the backend rejected.
  const submitErrorField =
    submitStatus.type !== "error"
      ? undefined
      : submitStatus.code === "email_taken"
        ? "email"
        : submitStatus.code?.startsWith("account_name_")
          ? "accountName"
          : undefined;
  const [prevSubmitStatusType, setPrevSubmitStatusType] = useState(
    submitStatus.type
  );
  if (prevSubmitStatusType !== submitStatus.type) {
    setPrevSubmitStatusType(submitStatus.type);
    const fieldMessage = localizeApiError(
      language,
      submitStatus.code,
      submitStatus.message
    );
    if (submitErrorField === "email") {
      setActiveStep(0);
      setEmailCheckError(fieldMessage);
    } else if (submitErrorField === "accountName") {
      setActiveStep(2);
      setAccountNameCheckError(fieldMessage);
    }
  }
  const submitErrorMessage =
    submitStatus.type === "error" && !submitErrorField
      ? localizeApiError(language, submitStatus.code, submitStatus.message)
      : "";

  useEffect(() => {
    if (submitStatus.type !== "error") {
      return;
    }
    const id = setTimeout(() => {
      fireEvent?.("clear-error");
    }, 5000);
    return () => clearTimeout(id);
  }, [submitStatus.type, fireEvent]);

  const resetForm = () => {
    setFormData(initialFormState);
    setTouchedFields({});
    setActiveStep(0);
    accountNameManuallyEdited.current = false;
  };

  const handleSuccessDialogClose = () => {
    fireEvent?.("close-success-dialog");
    resetForm();
  };

  const markTouched = (field: keyof TouchedState) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
  };

  // Track manual edits to account name
  const handleAccountNameChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = transliterateAccountName(e.target.value);
    const sanitizedCompanyName = sanitizeAccountName(formData.companyName);
    // Clearing the field hands it back to auto-fill from the company name.
    if (!value.trim()) {
      accountNameManuallyEdited.current = false;
    } else if (value !== sanitizedCompanyName) {
      accountNameManuallyEdited.current = true;
    }
    setFormData((prev) => ({ ...prev, accountName: value }));
    markTouched("accountName");
    // Only ask the backend about names that pass the local check, or the same error shows twice.
    if (charCount(value) >= 2 && ACCOUNT_NAME_PATTERN.test(value.trim())) {
      checkAccountNameAvailabilityDebounced(value.trim());
    } else {
      cancelAccountNameAvailabilityCheck();
      setAccountNameCheckError("");
    }
  };

  // Validation functions
  const emailError = (() => {
    if (activeStep !== 0) return undefined;
    if (!formData.email.trim()) {
      if (!touchedFields.email) return undefined;
      return t("auth.registration.errors.emailRequired", "Email is required.");
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      return t(
        "auth.registration.errors.emailInvalid",
        "Please enter a valid email address."
      );
    }
    // Keycloak and mail delivery are not verified for internationalized addresses.
    if (!/^[\x21-\x7E]+$/.test(formData.email.trim())) {
      return t(
        "auth.registration.errors.emailNotAscii",
        "Email address can only contain Latin letters (a-z), numbers, and symbols."
      );
    }
    return undefined;
  })();

  const firstNameError = (() => {
    if (activeStep !== 1) return undefined;
    if (!formData.firstName.trim()) {
      if (!touchedFields.firstName) return undefined;
      return t(
        "auth.registration.errors.firstNameRequired",
        "First name is required."
      );
    }
    if (charCount(formData.firstName) < 2) {
      return t(
        "auth.registration.errors.firstNameTooShort",
        "First name must be at least 2 characters."
      );
    }
    if (!PERSON_NAME_PATTERN.test(formData.firstName.trim())) {
      return t(
        "auth.registration.errors.firstNameInvalid",
        "First name can only contain letters, spaces, hyphens, and apostrophes."
      );
    }
    return undefined;
  })();

  const lastNameError = (() => {
    if (activeStep !== 1) return undefined;
    if (!formData.lastName.trim()) {
      if (!touchedFields.lastName) return undefined;
      return t(
        "auth.registration.errors.lastNameRequired",
        "Last name is required."
      );
    }
    if (charCount(formData.lastName) < 2) {
      return t(
        "auth.registration.errors.lastNameTooShort",
        "Last name must be at least 2 characters."
      );
    }
    if (!PERSON_NAME_PATTERN.test(formData.lastName.trim())) {
      return t(
        "auth.registration.errors.lastNameInvalid",
        "Last name can only contain letters, spaces, hyphens, and apostrophes."
      );
    }
    return undefined;
  })();

  const passwordError = (() => {
    if (activeStep !== 1) return undefined;
    if (!formData.password) {
      if (!touchedFields.password) return undefined;
      return t(
        "auth.registration.errors.passwordRequired",
        "Password is required."
      );
    }
    if (formData.password.length < 8) {
      return t(
        "auth.registration.errors.passwordTooShort",
        "Password must be at least 8 characters."
      );
    }
    if (!PASSWORD_PATTERN.test(formData.password)) {
      return t(
        "auth.registration.errors.passwordNotLatin",
        "Password can only contain Latin letters (a-z), numbers, and symbols."
      );
    }
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      return t(
        "auth.registration.errors.passwordInvalid",
        "Password must contain at least one uppercase letter, one lowercase letter, and one number."
      );
    }
    return undefined;
  })();

  const confirmPasswordError = (() => {
    if (activeStep !== 1) return undefined;
    if (!formData.confirmPassword) {
      if (!touchedFields.confirmPassword) return undefined;
      return t(
        "auth.registration.errors.confirmPasswordRequired",
        "Please confirm your password."
      );
    }
    if (formData.confirmPassword !== formData.password) {
      return t(
        "auth.registration.errors.passwordsMismatch",
        "Passwords do not match."
      );
    }
    return undefined;
  })();

  const companyNameError = (() => {
    if (activeStep !== 2) return undefined;
    if (!formData.companyName.trim()) {
      if (!touchedFields.companyName) return undefined;
      return t(
        "auth.registration.errors.companyNameRequired",
        "Company name is required."
      );
    }
    if (charCount(formData.companyName) < 2) {
      return t(
        "auth.registration.errors.companyNameTooShort",
        "Company name must be at least 2 characters."
      );
    }
    if (!COMPANY_NAME_PATTERN.test(formData.companyName.trim())) {
      return t(
        "auth.registration.errors.companyNameInvalid",
        "Company name can only contain letters, numbers, spaces, and & . , ' - ( )."
      );
    }
    return undefined;
  })();

  const accountNameError = (() => {
    if (activeStep !== 2) return undefined;
    if (!formData.accountName.trim()) {
      if (!touchedFields.accountName) return undefined;
      return t(
        "auth.registration.errors.accountNameRequired",
        "Account name is required."
      );
    }
    if (charCount(formData.accountName) < 2) {
      return t(
        "auth.registration.errors.accountNameTooShort",
        "Account name must be at least 2 characters."
      );
    }
    if (!ACCOUNT_NAME_PATTERN.test(formData.accountName.trim())) {
      return t(
        "auth.registration.errors.accountNameInvalid",
        "Account name can only contain Latin letters (including umlauts), numbers, spaces, and hyphens."
      );
    }
    return undefined;
  })();

  const isCurrentStepValid = (() => {
    switch (activeStep) {
      case 0:
        return !emailError && formData.email.trim().length > 0;
      case 1:
        return (
          !firstNameError &&
          !lastNameError &&
          !passwordError &&
          !confirmPasswordError &&
          formData.firstName.trim().length > 0 &&
          formData.lastName.trim().length > 0 &&
          formData.password.length > 0 &&
          formData.confirmPassword.length > 0
        );
      case 2:
        return (
          !companyNameError &&
          !accountNameError &&
          !accountNameCheckError &&
          formData.companyName.trim().length > 0 &&
          formData.accountName.trim().length > 0
        );
      default:
        return true;
    }
  })();

  const handleNext = async () => {
    if (!isCurrentStepValid) {
      switch (activeStep) {
        case 0:
          markTouched("email");
          break;
        case 1:
          markTouched("firstName");
          markTouched("lastName");
          markTouched("password");
          markTouched("confirmPassword");
          break;
        case 2:
          markTouched("companyName");
          markTouched("accountName");
          break;
      }
      return;
    }

    // Check email availability on step 0 before proceeding
    if (activeStep === 0) {
      setEmailCheckError("");
      setIsCheckingEmail(true);
      emailCheckRequestRef.current += 1;
      const requestId = emailCheckRequestRef.current;
      const emailCheck = await checkEmailAvailability(
        formData.email.trim().toLowerCase()
      );

      if (emailCheckRequestRef.current !== requestId) {
        return;
      }
      setIsCheckingEmail(false);

      if (!emailCheck.available) {
        setEmailCheckError(
          localizeApiError(
            language,
            emailCheck.error?.code,
            emailCheck.message ||
              t(
                "auth.registration.errors.emailAlreadyExists",
                "User with this email already exists"
              )
          )
        );
        return;
      }
    }

    // Check account name availability on step 2 before proceeding
    if (activeStep === 2) {
      setAccountNameCheckError("");
      setIsCheckingAccountName(true);
      const accountNameCheck = await checkAccountNameAvailability(
        formData.accountName.trim()
      );
      setIsCheckingAccountName(false);

      if (!accountNameCheck.available) {
        setAccountNameCheckError(
          localizeApiError(
            language,
            accountNameCheck.error?.code,
            accountNameCheck.message ||
              `Account "${formData.accountName}" already exists`
          )
        );
        return;
      }
    }

    // Clear errors when leaving steps
    if (activeStep === 0) {
      setEmailCheckError("");
    }
    if (activeStep === 2) {
      setAccountNameCheckError("");
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    // Clear errors when navigating back
    setEmailCheckError("");
    setAccountNameCheckError("");
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    // Check account name availability before submitting
    setAccountNameCheckError("");
    setIsCheckingAccountName(true);
    const accountNameCheck = await checkAccountNameAvailability(
      formData.accountName.trim()
    );
    setIsCheckingAccountName(false);

    if (!accountNameCheck.available) {
      setAccountNameCheckError(
        localizeApiError(
          language,
          accountNameCheck.error?.code,
          accountNameCheck.message ||
            `Account "${formData.accountName}" already exists`
        )
      );
      return;
    }

    const sanitizedData: RegistrationFormData = {
      email: formData.email.trim().toLowerCase(),
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      password: formData.password,
      confirmPassword: formData.confirmPassword,
      companyName: formData.companyName.trim(),
      accountName: formData.accountName.trim(),
    };
    fireEvent?.("register", { register: sanitizedData });
  };

  const renderProgressBar = () => (
    <Box display="flex" gap={1} flexGrow={1} minWidth={0} mx={2}>
      {STEPS.map((_, index) => (
        <Box
          key={index}
          sx={{
            flex: 1,
            height: 4,
            borderRadius: 999,
            backgroundColor: index <= activeStep ? "#3ec6ff" : "#d9f4ff",
          }}
        />
      ))}
    </Box>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mb: 3, width: "100%", maxWidth: 500 }}>
            <Typography
              component="label"
              htmlFor="email"
              sx={{
                display: "block",
                textAlign: "left",
                marginBottom: 1,
                fontWeight: 600,
                fontSize: "1rem",
                color: "#222",
              }}
            >
              {t("auth.registration.labels.emailAddress", "Email Address")}
            </Typography>
            <TextField
              id="email"
              value={formData.email}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, email: e.target.value }));
                markTouched("email");
                emailCheckRequestRef.current += 1;
                setIsCheckingEmail(false);
                setEmailCheckError("");
              }}
              variant="outlined"
              fullWidth
              type="text"
              disabled={isSubmitting}
              inputProps={{
                inputMode: "email",
                autoComplete: "email",
                autoCapitalize: "none",
                autoCorrect: "off",
                spellCheck: false,
                maxLength: MAX_LENGTH.email,
                style: { fontSize: "1rem", padding: "10px 12px" },
              }}
              sx={{
                mb: 0,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 4,
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#b3eaff",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#b3eaff",
                  },
                },
                "& .MuiInputBase-input": { background: "#fff" },
              }}
              autoFocus
            />
          </Box>
        );

      case 1:
        return (
          <>
            <Box sx={{ mb: 3, width: "100%", maxWidth: 500 }}>
              <Typography
                component="label"
                htmlFor="firstName"
                sx={{
                  display: "block",
                  textAlign: "left",
                  marginBottom: 1,
                  fontWeight: 600,
                  fontSize: "1rem",
                  color: "#222",
                }}
              >
                {t("auth.registration.labels.firstName", "First Name")}
              </Typography>
              <TextField
                id="firstName"
                value={formData.firstName}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    firstName: e.target.value,
                  }));
                  markTouched("firstName");
                }}
                onBlur={() => markTouched("firstName")}
                variant="outlined"
                fullWidth
                disabled={isSubmitting}
                inputProps={{
                  maxLength: MAX_LENGTH.personName,
                  style: { fontSize: "1rem", padding: "10px 12px" },
                }}
                sx={{
                  mb: 0,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 4,
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b3eaff",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b3eaff",
                    },
                  },
                  "& .MuiInputBase-input": { background: "#fff" },
                }}
                autoFocus
              />
            </Box>

            <Box sx={{ mb: 3, width: "100%", maxWidth: 500 }}>
              <Typography
                component="label"
                htmlFor="lastName"
                sx={{
                  display: "block",
                  textAlign: "left",
                  marginBottom: 1,
                  fontWeight: 600,
                  fontSize: "1rem",
                  color: "#222",
                }}
              >
                {t("auth.registration.labels.lastName", "Last Name")}
              </Typography>
              <TextField
                id="lastName"
                value={formData.lastName}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    lastName: e.target.value,
                  }));
                  markTouched("lastName");
                }}
                onBlur={() => markTouched("lastName")}
                variant="outlined"
                fullWidth
                disabled={isSubmitting}
                inputProps={{
                  maxLength: MAX_LENGTH.personName,
                  style: { fontSize: "1rem", padding: "10px 12px" },
                }}
                sx={{
                  mb: 0,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 4,
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b3eaff",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b3eaff",
                    },
                  },
                  "& .MuiInputBase-input": { background: "#fff" },
                }}
              />
            </Box>

            <Box sx={{ mb: 3, width: "100%", maxWidth: 500 }}>
              <Typography
                component="label"
                htmlFor="password"
                sx={{
                  display: "block",
                  textAlign: "left",
                  marginBottom: 1,
                  fontWeight: 600,
                  fontSize: "1rem",
                  color: "#222",
                }}
              >
                {t("auth.registration.labels.password", "Password")}
              </Typography>
              <TextField
                id="password"
                value={formData.password}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    password: e.target.value,
                  }));
                  markTouched("password");
                }}
                onBlur={() => markTouched("password")}
                variant="outlined"
                fullWidth
                type="password"
                disabled={isSubmitting}
                inputProps={{
                  style: { fontSize: "1rem", padding: "10px 12px" },
                }}
                sx={{
                  mb: 0,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 4,
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b3eaff",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b3eaff",
                    },
                  },
                  "& .MuiInputBase-input": { background: "#fff" },
                }}
              />
            </Box>

            <Box sx={{ mb: 3, width: "100%", maxWidth: 500 }}>
              <Typography
                component="label"
                htmlFor="confirmPassword"
                sx={{
                  display: "block",
                  textAlign: "left",
                  marginBottom: 1,
                  fontWeight: 600,
                  fontSize: "1rem",
                  color: "#222",
                }}
              >
                {t(
                  "auth.registration.labels.confirmPassword",
                  "Confirm Password"
                )}
              </Typography>
              <TextField
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    confirmPassword: e.target.value,
                  }));
                  markTouched("confirmPassword");
                }}
                onBlur={() => markTouched("confirmPassword")}
                variant="outlined"
                fullWidth
                type="password"
                disabled={isSubmitting}
                inputProps={{
                  style: { fontSize: "1rem", padding: "10px 12px" },
                }}
                sx={{
                  mb: 0,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 4,
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b3eaff",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b3eaff",
                    },
                  },
                  "& .MuiInputBase-input": { background: "#fff" },
                }}
              />
            </Box>
          </>
        );

      case 2:
        return (
          <>
            <Box sx={{ mb: 3, width: "100%", maxWidth: 500 }}>
              <Typography
                component="label"
                htmlFor="companyName"
                sx={{
                  display: "block",
                  textAlign: "left",
                  marginBottom: 1,
                  fontWeight: 600,
                  fontSize: "1rem",
                  color: "#222",
                }}
              >
                {t("auth.registration.labels.companyName", "Company name")}
              </Typography>
              <TextField
                id="companyName"
                value={formData.companyName}
                onChange={(e) => {
                  handleCompanyNameChange(e.target.value);
                  markTouched("companyName");
                }}
                onBlur={() => markTouched("companyName")}
                variant="outlined"
                fullWidth
                disabled={isSubmitting}
                inputProps={{
                  maxLength: MAX_LENGTH.companyName,
                  style: { fontSize: "1rem", padding: "10px 12px" },
                }}
                sx={{
                  mb: 0,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 4,
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b3eaff",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b3eaff",
                    },
                  },
                  "& .MuiInputBase-input": { background: "#fff" },
                }}
                autoFocus
              />
            </Box>

            <Box sx={{ mb: 3, width: "100%", maxWidth: 500 }}>
              <Typography
                component="label"
                htmlFor="accountName"
                sx={{
                  display: "block",
                  textAlign: "left",
                  marginBottom: 1,
                  fontWeight: 600,
                  fontSize: "1rem",
                  color: "#222",
                }}
              >
                {t("auth.registration.labels.accountName", "Account Name")}
              </Typography>
              <TextField
                id="accountName"
                value={formData.accountName}
                onChange={handleAccountNameChange}
                onBlur={() => markTouched("accountName")}
                variant="outlined"
                fullWidth
                disabled={isSubmitting}
                inputProps={{
                  maxLength: MAX_LENGTH.accountName,
                  style: { fontSize: "1rem", padding: "10px 12px" },
                }}
                sx={{
                  mb: 0,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 4,
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b3eaff",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b3eaff",
                    },
                  },
                  "& .MuiInputBase-input": { background: "#fff" },
                }}
              />
            </Box>
          </>
        );

      default:
        return null;
    }
  };

  const renderNavigation = () => (
    <Box
      sx={{
        width: "100%",
        maxWidth: 500,
        mx: "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Button
        variant="text"
        onClick={handleBack}
        disabled={isSubmitting || activeStep === 0}
        sx={{
          textTransform: "none",
          color: "#374151",
          fontWeight: 500,
          minWidth: "auto",
          px: 2,
          "&:hover": {
            backgroundColor: "rgba(29, 161, 242, 0.1)",
            color: "#374151",
          },
        }}
      >
        {t("auth.registration.buttons.back", "Back")}
      </Button>
      {renderProgressBar()}
      <Button
        variant="contained"
        onClick={activeStep === STEPS.length - 1 ? handleSubmit : handleNext}
        disabled={
          !isCurrentStepValid ||
          isSubmitting ||
          isCheckingEmail ||
          isCheckingAccountName
        }
        sx={{
          backgroundColor: "#3ec6ff",
          color: "#ffffff",
          textTransform: "none",
          fontWeight: 500,
          fontSize: "1rem",
          borderRadius: "6px",
          minWidth: 108,
          minHeight: 42,
          px: 2.5,
          py: 0,
          boxShadow: "none",
          "&:hover": {
            backgroundColor: "#1da1f2",
            boxShadow: "none",
          },
          "&:disabled": {
            backgroundColor: "#d1d5db",
            color: "#9ca3af",
          },
        }}
      >
        {activeStep === STEPS.length - 1
          ? isSubmitting
            ? t("auth.registration.buttons.submitting", "Submitting...")
            : t("auth.registration.buttons.register", "Register Now")
          : isCheckingEmail && activeStep === 0
            ? t("auth.registration.buttons.checking", "Checking...")
            : isCheckingAccountName && activeStep === 2
              ? t("auth.registration.buttons.checking", "Checking...")
              : t("auth.registration.buttons.next", "Next")}
      </Button>
    </Box>
  );

  // Get current step errors for display
  const getCurrentStepErrors = () => {
    const errors: string[] = [];
    switch (activeStep) {
      case 0:
        if (emailError) errors.push(emailError);
        break;
      case 1:
        if (firstNameError) errors.push(firstNameError);
        if (lastNameError) errors.push(lastNameError);
        if (passwordError) errors.push(passwordError);
        if (confirmPasswordError) errors.push(confirmPasswordError);
        break;
      case 2:
        if (companyNameError) errors.push(companyNameError);
        if (accountNameError) errors.push(accountNameError);
        break;
    }
    return errors;
  };

  const currentStepErrors = getCurrentStepErrors();

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#e4e9fa",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "-8px",
          position: "relative",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 800,
            width: "100%",
            borderRadius: "24px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            p: { xs: "24px", sm: "48px 64px" },
            mx: "auto",
            bgcolor: "#fff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            maxHeight: "calc(100vh - 80px)",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: { xs: 28, sm: 36 },
              left: { xs: 24, sm: 32 },
              right: { xs: 24, sm: 32 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box
              component="img"
              src="/pimo-large.svg"
              alt="Pimo Logo"
              sx={{
                height: 45,
                width: 125,
                display: "block",
                objectFit: "contain",
              }}
            />

            <FormControl size="small" sx={{ minWidth: 0 }}>
              <Select
                value={language}
                onChange={(e) => {
                  const newLang = e.target.value as SupportedLanguage;
                  fireEvent?.("change-language", { language: newLang });
                }}
                displayEmpty
                sx={{
                  fontFamily:
                    "'Inter', Arial, 'Noto Color Emoji', 'Apple Color Emoji', 'Segoe UI Emoji', sans-serif",
                  fontSize: "0.95rem",
                  borderRadius: "6px",
                  height: 34,
                  minWidth: 118,
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    padding: "6px 42px 6px 12px !important",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #bbb",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#bbb",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#bbb",
                  },
                  "& .MuiSelect-icon": {
                    right: 12,
                  },
                  background: "#fff",
                }}
                inputProps={{
                  "aria-label": t("userMenu.language", "Language"),
                }}
              >
                {languageOptions.map((option) => (
                  <MenuItem key={option.code} value={option.code}>
                    {t(`userMenu.${option.label}`, option.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {activeStep === 0 && (
            <>
              <Typography
                sx={{
                  fontSize: { xs: "2rem", sm: "2.5rem" },
                  fontWeight: 600,
                  mb: 0.5,
                  mt: { xs: 4, sm: 5 },
                  color: "#222",
                  textAlign: "center",
                  width: "100%",
                  flexShrink: 0,
                }}
              >
                {t("auth.registration.title", "Welcome to Pimo")}
              </Typography>
              <Typography
                sx={{
                  color: "#888",
                  fontWeight: 400,
                  fontSize: "1.25rem",
                  textAlign: "center",
                  mb: 3,
                  mt: 0,
                  flexShrink: 0,
                }}
              >
                {t("auth.registration.subtitle", "start now for free")}
              </Typography>
            </>
          )}

          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              overflowY: "auto",
              flex: 1,
              minHeight: 0,
              mt: activeStep !== 0 ? { xs: 4, sm: 5 } : 0,
            }}
          >
            {(emailCheckError ||
              accountNameCheckError ||
              submitErrorMessage ||
              currentStepErrors.length > 0) && (
              <Box sx={{ width: "100%", maxWidth: 500, mb: 3 }}>
                <Box
                  sx={{
                    borderRadius: 2,
                    backgroundColor: "#fff6f6",
                    border: "1px solid #ff6b6b",
                    color: "#ff3b3b",
                    textAlign: "center",
                    fontSize: "0.95rem",
                    fontFamily: "'Inter', Arial, sans-serif",
                    fontWeight: 400,
                    lineHeight: 1.4,
                    px: 3,
                    py: 1.5,
                  }}
                >
                  {emailCheckError && (
                    <Box
                      sx={{
                        mb:
                          accountNameCheckError ||
                          currentStepErrors.length > 0 ||
                          submitErrorMessage
                            ? 1
                            : 0,
                      }}
                    >
                      {emailCheckError}
                    </Box>
                  )}
                  {accountNameCheckError && (
                    <Box
                      sx={{
                        mb:
                          currentStepErrors.length > 0 || submitErrorMessage
                            ? 1
                            : 0,
                      }}
                    >
                      {accountNameCheckError}
                    </Box>
                  )}
                  {submitErrorMessage && (
                    <Box sx={{ mb: currentStepErrors.length > 0 ? 1 : 0 }}>
                      {submitErrorMessage}
                    </Box>
                  )}
                  {currentStepErrors.length > 0 && (
                    <Box>
                      {currentStepErrors.map((error, index) => (
                        <Box
                          key={index}
                          sx={{
                            mb: index < currentStepErrors.length - 1 ? 0.5 : 0,
                          }}
                        >
                          {error}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {renderStepContent()}

            {activeStep === 0 && (
              <>
                <Typography
                  sx={{
                    color: "#222",
                    fontSize: 16,
                    mt: 3,
                    textAlign: "center",
                    fontWeight: 400,
                  }}
                >
                  {t(
                    "auth.registration.helper.termsIntro",
                    "By continuing, you agree to the following"
                  )}
                  <br />
                  <Link
                    href="https://www.pimoapp.com/rechliches-und-datenschutz"
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                    sx={{ color: "#1da1f2", fontWeight: 500 }}
                  >
                    {t("legalFooter.termsOfUse", "Terms of Use")}
                  </Link>{" "}
                  {t("auth.registration.helper.and", "and")}{" "}
                  <Link
                    href="https://www.pimoapp.com/rechliches-und-datenschutz"
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                    sx={{ color: "#1da1f2", fontWeight: 500 }}
                  >
                    {t("legalFooter.privacyPolicy", "Privacy Policy")}
                  </Link>
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    maxWidth: 350,
                    my: 3,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      height: 2,
                      bgcolor: "#b0b0b0",
                      minWidth: 40,
                    }}
                  />
                  <Typography
                    sx={{ mx: 2, color: "#bbb", fontSize: 16, fontWeight: 400 }}
                  >
                    or
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      height: 2,
                      bgcolor: "#b0b0b0",
                      minWidth: 40,
                    }}
                  />
                </Box>

                <Box sx={{ textAlign: "center", width: "100%", mb: 4 }}>
                  <Button
                    component="a"
                    href={`${BACKEND_URL}/keycloak/login?ui_locales=${encodeURIComponent(language)}`}
                    variant="text"
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      color: "#1da1f2",
                      fontWeight: 500,
                      fontSize: "1rem",
                      fontFamily: "'Inter', Arial, sans-serif",
                      textTransform: "none",
                      borderRadius: "8px",
                      px: 2,
                      py: 0.75,
                      "&:hover": {
                        backgroundColor: "rgba(29, 161, 242, 0.08)",
                      },
                    }}
                  >
                    {t(
                      "auth.registration.helper.alreadyHaveAccount",
                      "Already have an account? Please login"
                    )}
                  </Button>
                </Box>
              </>
            )}
          </Box>

          <Box
            sx={{
              width: "100%",
              flexShrink: 0,
              mt: 2,
            }}
          >
            {renderNavigation()}
          </Box>
        </Paper>
      </Box>

      <SuccessDialog
        open={showSuccessDialog}
        onClose={handleSuccessDialogClose}
        language={language}
      />
    </>
  );
};
